/**
 * VoiceProvider — unified voice I/O context.
 *
 * TTS lifecycle: on every user_message, kill old TTS WebSocket + open fresh one.
 * No persistent connection. No gate/muted hacks. Clean.
 *
 * STT: InputEngine (AudioWorklet mic) → Cartesia STT WebSocket.
 */

import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { OutputEngine } from './OutputEngine';
import { InputEngine } from './InputEngine';
import { createCartesiaTTS, createHumeTTS } from './ttsProviders';
import { createCartesiaSTT, createDeepgramSTT } from './sttProvider';
import { stripVoiceTags } from './stripForTTS';
import chatEvents from '../chatEvents';

export const VoiceContext = createContext(null);

export function useVoice() {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within <VoiceProvider>');
  return ctx;
}

export function VoiceProvider({ apiUrl, gptId, shareToken, lang, children }) {
  const [enabled, setEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [transcript, setTranscript] = useState('');

  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const ttsRef = useRef(null);
  const sttRef = useRef(null);
  const browserRecognitionRef = useRef(null);
  const langRef = useRef(lang);
  const gptIdRef = useRef(gptId);
  const apiUrlRef = useRef(apiUrl);
  const shareTokenRef = useRef(shareToken);
  const enabledRef = useRef(false);
  const mountedRef = useRef(true);
  const pendingRef = useRef([]);
  const streamEndedRef = useRef(false);
  const ttsConnectingRef = useRef(false);

  // Keep refs in sync
  if (lang !== undefined) langRef.current = lang;
  gptIdRef.current = gptId;
  apiUrlRef.current = apiUrl;
  shareTokenRef.current = shareToken;
  enabledRef.current = enabled;

  // ── Token fetching ──

  async function fetchVoiceToken() {
    const id = gptIdRef.current;
    if (!id) return null;
    try {
      const base = apiUrlRef.current || '';
      if (shareTokenRef.current) {
        const r = await fetch(`${base}/public/voice-token/${id}?token=${encodeURIComponent(shareTokenRef.current)}`);
        return r.ok ? await r.json() : null;
      }
      const t = localStorage.getItem('cme_token');
      const r = await fetch(`${base}/custom-gpts/${id}/voice-token`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  }

  async function fetchMicToken() {
    const id = gptIdRef.current;
    if (!id) return null;
    try {
      const base = apiUrlRef.current || '';
      if (shareTokenRef.current) {
        const r = await fetch(`${base}/public/microphone-token/${id}?token=${encodeURIComponent(shareTokenRef.current)}`);
        return r.ok ? await r.json() : null;
      }
      const t = localStorage.getItem('cme_token');
      const r = await fetch(`${base}/custom-gpts/${id}/microphone-token`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  }

  // ── Pre-cached tokens (fetched on mount, refreshed every 4 min) ──

  const cachedMicTokenRef = useRef(null);
  const micRefreshIntervalRef = useRef(null);
  const cachedVoiceTokenRef = useRef(null);

  async function refreshMicToken() {
    cachedMicTokenRef.current = await fetchMicToken();
    console.log('[Voice] mic token refreshed:', !!cachedMicTokenRef.current);
  }

  async function refreshVoiceToken() {
    cachedVoiceTokenRef.current = await fetchVoiceToken();
    console.log('[Voice] voice token refreshed:', !!cachedVoiceTokenRef.current);
  }

  // Mic token is fetched on-demand in startListening (no eager polling).
  // This avoids 404s for GPTs that don't have the Microphone tool installed.
  // Reset mic cache when gptId changes (different GPT may not have mic tool).
  useEffect(() => {
    cachedMicTokenRef.current = null;
    if (micRefreshIntervalRef.current) {
      clearInterval(micRefreshIntervalRef.current);
      micRefreshIntervalRef.current = null;
    }
  }, [gptId]);

  // Pre-fetch voice token when speaker is enabled
  useEffect(() => {
    if (!enabled || !gptId) { cachedVoiceTokenRef.current = null; return; }
    refreshVoiceToken();
    const interval = setInterval(refreshVoiceToken, 4 * 60 * 1000);
    return () => { clearInterval(interval); cachedVoiceTokenRef.current = null; };
  }, [enabled, gptId]);

  // ── Output Engine (persistent — never destroyed) ──

  async function ensureOutput(sampleRate) {
    console.log('[Voice] ensureOutput, existing:', !!outputRef.current);
    if (!outputRef.current) outputRef.current = new OutputEngine();
    const output = outputRef.current;
    await output.init(sampleRate);
    console.log('[Voice] ensureOutput done, ctx state:', output.context?.state);

    let speakingTimer = null;
    output.onPlaybackStarted = () => {
      if (speakingTimer) { clearTimeout(speakingTimer); speakingTimer = null; }
      if (mountedRef.current) setSpeaking(true);
    };
    output.onPlaybackEnded = () => {
      if (speakingTimer) clearTimeout(speakingTimer);
      speakingTimer = setTimeout(() => {
        if (mountedRef.current) setSpeaking(false);
        speakingTimer = null;
      }, 500);
    };
    return output;
  }

  // ── TTS WebSocket (created fresh on each user_message) ──

  function killTTS() {
    console.log('[Voice] killTTS, had:', !!ttsRef.current);
    ttsRef.current?.disconnect();
    ttsRef.current = null;
    ttsConnectingRef.current = false;
    pendingRef.current = [];
    streamEndedRef.current = false;
  }

  async function openTTS() {
    console.log('[Voice] openTTS starting');
    killTTS();
    ttsConnectingRef.current = true;

    try {
      // Use cached token if available, otherwise fetch fresh
      const cached = !!cachedVoiceTokenRef.current;
      const data = cachedVoiceTokenRef.current || await fetchVoiceToken();
      console.log('[Voice] openTTS token:', cached ? 'CACHED' : 'FETCHED');
      if (!data || !mountedRef.current) return;

      const providerName = data.provider || 'cartesia';
      const sr = providerName === 'hume' ? 48000 : 44100;
      const output = await ensureOutput(sr);

      const playAudio = (float32) => {
        console.log('[Voice] playAudio chunk, samples:', float32.length);
        output.play(float32);
      };

      const tts = providerName === 'hume'
        ? createHumeTTS(playAudio)
        : createCartesiaTTS(playAudio);

      tts.onOpen = () => {
        console.log('[Voice] TTS WS opened, pending:', pendingRef.current.length, 'streamEnded:', streamEndedRef.current);
        const items = pendingRef.current;
        pendingRef.current = [];
        for (const text of items) tts.bufferText(text);
        if (streamEndedRef.current) {
          streamEndedRef.current = false;
          tts.finishStream();
        }
      };
      if (tts.setLang) tts.setLang(langRef.current);
      tts.connect(data, () => {
        // Token refresh — reconnect
        if (mountedRef.current && enabledRef.current) openTTS();
      });
      ttsRef.current = tts;
    } finally {
      ttsConnectingRef.current = false;
    }
  }

  // ── Text handling ──

  function feedText(rawText) {
    if (!rawText) return;
    console.log('[Voice] feedText, tts:', !!ttsRef.current, 'ready:', ttsRef.current?.isReady(), 'connecting:', ttsConnectingRef.current);
    const tts = ttsRef.current;
    if (tts?.isReady()) {
      tts.bufferText(rawText);
    } else {
      pendingRef.current.push(rawText);
    }
  }

  function finishStream() {
    const tts = ttsRef.current;
    if (tts?.isReady()) {
      tts.finishStream();
    } else {
      streamEndedRef.current = true;
    }
  }

  // ── Called on every user_message: kill old TTS, open fresh one ──

  function onNewMessage() {
    // Stop any playing audio
    outputRef.current?.interrupt();
    // Kill old TTS WebSocket, open fresh one
    openTTS();
  }

  // ── Input (STT) ──

  const startListening = useCallback(async () => {
    if (listening || connecting) return false;

    setConnecting(true);

    try {
      // Stop any playing audio
      outputRef.current?.interrupt();
      killTTS();

      // Use cached token if available, otherwise fetch fresh
      const cachedMic = !!cachedMicTokenRef.current;
      const micData = cachedMicTokenRef.current || await fetchMicToken();
      if (micData && !cachedMic) {
        // Cache and start refresh interval now that we know the tool exists
        cachedMicTokenRef.current = micData;
        if (!micRefreshIntervalRef.current) {
          micRefreshIntervalRef.current = setInterval(refreshMicToken, 4 * 60 * 1000);
        }
      }
      console.log('[Voice] startListening token:', cachedMic ? 'CACHED' : 'FETCHED');

      // If no token (Microphone tool not installed), fall back to browser SpeechRecognition
      if (!micData?.token) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) { setConnecting(false); return false; }

        console.log('[Voice] Falling back to browser SpeechRecognition');
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = langRef.current || 'en-US';

        recognition.onresult = (event) => {
          let text = '';
          for (let i = 0; i < event.results.length; i++) {
            text += event.results[i][0].transcript;
          }
          setTranscript(text);
          if (event.results[event.results.length - 1].isFinal) {
            _stopListeningInternal();
          }
        };
        recognition.onerror = () => _stopListeningInternal();
        recognition.onend = () => _stopListeningInternal();

        browserRecognitionRef.current = recognition;
        recognition.start();
        setListening(true);
        setTranscript('');
        return true;
      }

      if (!inputRef.current) inputRef.current = new InputEngine();
      const input = inputRef.current;
      const ok = await input.start();
      if (!ok) { setConnecting(false); return false; }

      const sttProvider = micData.provider === 'deepgram' ? createDeepgramSTT : createCartesiaSTT;
      if (!sttRef.current) sttRef.current = sttProvider();
      const stt = sttRef.current;

      let lastTranscript = '';
      console.log('[Voice] STT connect, lang:', langRef.current);
      const connected = await stt.connect(micData.token, langRef.current, ({ text, isFinal }) => {
        if (text) {
          lastTranscript = text;
          setTranscript(text);
        }
        if (isFinal && lastTranscript.trim()) {
          _stopListeningInternal();
        }
      });

      if (!connected) {
        input.stop();
        setConnecting(false);
        return false;
      }

      input.onAudio = (int16) => stt.sendAudio(int16);

      setListening(true);
      setTranscript('');
      return true;
    } finally {
      setConnecting(false);
    }
  }, [listening, connecting]);

  function _stopListeningInternal() {
    if (browserRecognitionRef.current) {
      browserRecognitionRef.current.stop();
      browserRecognitionRef.current = null;
    }
    if (inputRef.current) inputRef.current.stop();
    if (sttRef.current) sttRef.current.disconnect();
    inputRef.current = null;
    sttRef.current = null;
    setListening(false);
  }

  const stopListening = useCallback(() => {
    _stopListeningInternal();
  }, []);

  // ── Enable/Disable ──

  const enable = useCallback(async () => {
    setEnabled(true);
    // Initialize OutputEngine NOW — during user gesture (click).
    // iOS requires AudioContext creation inside a user gesture.
    const provider = cachedVoiceTokenRef.current?.provider || 'cartesia';
    const sr = provider === 'hume' ? 48000 : 44100;
    await ensureOutput(sr);
  }, []);

  const disable = useCallback(() => {
    killTTS();
    outputRef.current?.interrupt();
    setEnabled(false);
    setSpeaking(false);
  }, []);

  const toggle = useCallback(() => {
    if (enabledRef.current) disable();
    else enable();
  }, [enable, disable]);

  // ── Unlock audio on first gesture when enabled ──

  useEffect(() => {
    if (!enabled) return;
    const unlock = () => outputRef.current?.unlock();
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true, passive: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [enabled]);

  // ── Listen to chatEvents ──

  useEffect(() => {
    const unregisterTransform = chatEvents.registerTextTransform(stripVoiceTags);

    function onText(e) {
      if (e.detail?.text && enabledRef.current) {
        feedText(e.detail.text);
      }
    }
    function onStreamEnd() {
      if (enabledRef.current) finishStream();
    }
    function onUserMessage() {
      console.log('[Voice] user_message, enabled:', enabledRef.current);
      if (enabledRef.current) onNewMessage();
    }

    chatEvents.addEventListener('tts_text', onText);
    chatEvents.addEventListener('tts_stream_end', onStreamEnd);
    chatEvents.addEventListener('user_message', onUserMessage);

    return () => {
      unregisterTransform();
      chatEvents.removeEventListener('tts_text', onText);
      chatEvents.removeEventListener('tts_stream_end', onStreamEnd);
      chatEvents.removeEventListener('user_message', onUserMessage);
    };
  }, [gptId]);

  // ── Update lang on TTS provider ──

  useEffect(() => {
    if (ttsRef.current?.setLang) ttsRef.current.setLang(lang);
  }, [lang]);

  // ── Debug: ?debug=true in URL enables log capture + copy button ──

  const [debugMode] = useState(() => new URLSearchParams(window.location.search).has('debug'));
  useEffect(() => {
    if (!debugMode) return;
    window._debugLogs = [];
    const _origLog = console.log;
    console.log = (...args) => {
      _origLog(...args);
      const msg = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
      if (msg.startsWith('[') && !msg.includes('playAudio chunk')) {
        window._debugLogs.push(msg);
        if (window._debugLogs.length > 500) window._debugLogs.shift();
      }
    };
    window._copyDebugLogs = () => {
      const text = window._debugLogs.join('\n');
      navigator.clipboard.writeText(text).then(() => alert('Logs copied!')).catch(() => {
        const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); alert('Logs copied!');
      });
    };
    return () => { console.log = _origLog; delete window._debugLogs; delete window._copyDebugLogs; };
  }, [debugMode]);

  // ── Cleanup on unmount ──

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      ttsRef.current?.disconnect();
      inputRef.current?.stop();
      sttRef.current?.disconnect();
      outputRef.current?.close();
      if (micRefreshIntervalRef.current) clearInterval(micRefreshIntervalRef.current);
    };
  }, []);

  // ── Context value ──

  const setLang = useCallback((l) => { langRef.current = l || ''; }, []);

  const value = {
    enabled,
    speaking,
    listening,
    connecting,
    transcript,
    enable,
    disable,
    toggle,
    setLang,
    startListening,
    stopListening,
    feedText,
    finishStream,
    interrupt: () => { killTTS(); outputRef.current?.interrupt(); },
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
      {debugMode && (
        <div style={{ position: 'fixed', top: 8, left: 8, zIndex: 99999 }}>
          <button onClick={() => window._copyDebugLogs?.()} style={{ fontSize: 10, padding: '4px 8px', opacity: 0.4, background: '#333', color: '#fff', border: 'none', borderRadius: 4 }}>Copy Logs</button>
        </div>
      )}
    </VoiceContext.Provider>
  );
}
